import { Capacitor } from '@capacitor/core';
import { SecurePreferences } from '@capawesome-team/capacitor-secure-preferences';

import { environment } from '../environment/environment';
import type { GuestInfo } from '../pages/guest/guestSlice';
import { CryptoService } from '../services/crypto';
import { RaygunErrorHandlerService } from '../services/raygun';

const { logError } = RaygunErrorHandlerService();

const THREAD_ID_KEY = '007406ee-615f-4187-bc3d-d989026b73c3';
const GUEST_INFO_KEY = '1b411c74-fb47-455c-b4d6-113d6d521ed4';
const GUEST_PREFERENCES_KEY = '8f2305e6-e578-4113-9b4b-9738af43d05b';

export const getGuestThreadId = async () => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return;
  }

  const { value: encrypted } = await SecurePreferences.get({ key: THREAD_ID_KEY });
  if (encrypted) {
    const decrypted = await CryptoService.decrypt(encrypted, environment.cryptoKey, 'getGuestThreadId');
    return decrypted;
  }
};

export const saveGuestThreadId = async (thread_id: string) => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return;
  }

  const encrypted = await CryptoService.encrypt(thread_id, environment.cryptoKey, 'saveGuestThreadId');
  await SecurePreferences.set({
    key: THREAD_ID_KEY,
    value: encrypted,
  });
};

export const removeGuestThreadId = async () => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return;
  }

  await SecurePreferences.remove({ key: THREAD_ID_KEY });
};

export const getOfflineGuestInfo = async (): Promise<GuestInfo | undefined> => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return;
  }

  const { value: encrypted } = await SecurePreferences.get({ key: GUEST_INFO_KEY });
  if (encrypted) {
    const decrypted = await CryptoService.decrypt(encrypted, environment.cryptoKey, 'getOfflineGuestInfo');
    try {
      const guestInfo = JSON.parse(decrypted);
      return guestInfo as GuestInfo;
    } catch (error) {
      logError(error, ['getOfflineGuestInfo']);
    }
  }
};

export const saveOfflineGuestInfo = async (guestInfo: GuestInfo) => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return;
  }

  const guestInfoStr = JSON.stringify(guestInfo);
  const encrypted = await CryptoService.encrypt(guestInfoStr, environment.cryptoKey, 'saveOfflineGuestInfo');
  await SecurePreferences.set({
    key: GUEST_INFO_KEY,
    value: encrypted,
  });
};

export const saveSessionGuestPreferences = (labelIds: string[]) => {
  sessionStorage.setItem(GUEST_PREFERENCES_KEY, JSON.stringify(labelIds));
};

export const getSessionGuestPreferences = (): string[] => {
  try {
    const labelIdsStr = sessionStorage.getItem(GUEST_PREFERENCES_KEY);
    if (labelIdsStr) {
      return JSON.parse(labelIdsStr) as string[];
    }
  } catch (error) {
    logError(error, ['getSessionGuestPreferences']);
  }

  return [];
};
